import React, { useEffect, useRef, useState } from "react";

type TooltipProps = {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: "right" | "left" | "top" | "bottom";
  className?: string;
};

const sidePosition = (side: TooltipProps["side"]) => {
  switch (side) {
    case "left":
      return "right-full mr-2 top-1/2 -translate-y-1/2";
    case "top":
      return "bottom-full mb-2 left-1/2 -translate-x-1/2";
    case "bottom":
      return "top-full mt-2 left-1/2 -translate-x-1/2";
    case "right":
    default:
      return "left-full ml-2 top-1/2 -translate-y-1/2";
  }
};

const Tooltip: React.FC<TooltipProps> = ({ content, children, side = "right", className = "" }) => {
  const [open, setOpen] = useState(false);
  const showTimer = useRef<number | null>(null);
  const hideTimer = useRef<number | null>(null);

  const onShow = () => {
    if (hideTimer.current) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
    if (!open) {
      showTimer.current = window.setTimeout(() => setOpen(true), 120);
    }
  };
  const onHide = () => {
    if (showTimer.current) {
      window.clearTimeout(showTimer.current);
      showTimer.current = null;
    }
    hideTimer.current = window.setTimeout(() => setOpen(false), 80);
  };

  useEffect(() => () => {
    if (showTimer.current) window.clearTimeout(showTimer.current);
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
  }, []);

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={onShow}
      onMouseLeave={onHide}
      onFocus={onShow}
      onBlur={onHide}
      tabIndex={0}
    >
      {children}
      <div
        className={[
          "pointer-events-none",
          open ? "visible opacity-100" : "invisible opacity-0",
          "transition-opacity duration-150",
          "absolute z-50",
          sidePosition(side),
          className,
        ].join(" ")}
      >
        <div role="tooltip" className="relative px-2.5 py-1.5 rounded-md text-sm text-white bg-slate-900/95 border border-white/10 shadow-lg">
          {content}
          <span
            aria-hidden
            className={[
              "absolute w-2 h-2 rotate-45 bg-slate-900/95 border border-white/10",
              side === "right" && "-left-1 top-1/2 -translate-y-1/2 border-l-0 border-t-0",
              side === "left" && "-right-1 top-1/2 -translate-y-1/2 border-r-0 border-b-0",
              side === "top" && "left-1/2 -bottom-1 -translate-x-1/2 border-b-0 border-l-0",
              side === "bottom" && "left-1/2 -top-1 -translate-x-1/2 border-t-0 border-r-0",
            ].filter(Boolean).join(" ")}
          />
        </div>
      </div>
    </div>
  );
};

export default Tooltip;
